/**
 * Regional prices and localized offers (docs/MONETIZATION-PLAN.md §4.3 + decisions 2026-10-18).
 *
 * Source of truth at runtime: native → the store via RevenueCat (`priceString`), web → Stripe Prices.
 * This table is the dev / mock source and the display fallback; it mirrors what the store consoles
 * should be configured with (nearest store price point). Pure: no DOM, Intl only.
 *
 * Tiers: tier 1 US/CA/AU/NZ (USD), GB (GBP), EU + Nordics (EUR, VAT included), VN (VND, own table);
 * every other known country is tier 2 (≈70 % of USD) or tier 3 (≈40 % of USD), priced in USD.
 */
import { PRICES_USD_CENTS, TRIAL_DAYS, type BillingPeriod, type PlanId } from './constants';
import type { L } from './text';
import { productId } from './entitlement';

export type PriceTier = 'tier1' | 'tier2' | 'tier3';
export type OfferingId = 'default' | 'onboarding' | 'invited_14d';

/** Prices in major units of `currency` (e.g. 9.99 USD, 699000 VND). */
export interface RegionPrices {
  currency: string;
  tier: PriceTier;
  prices: Record<PlanId, Record<BillingPeriod, number>>;
}

const usd = (cents: number) => cents / 100;
/** Ratio of the USD price rounded to a ".99" price point (6.993 → 6.99, 9.093 → 8.99). */
const ratioPrice = (cents: number, ratio: number) => Math.max(0.99, Math.round(usd(cents) * ratio) - 0.01);
const scaled = (ratio: number): RegionPrices['prices'] => ({
  individual: { monthly: ratioPrice(PRICES_USD_CENTS.individual.monthly, ratio), yearly: ratioPrice(PRICES_USD_CENTS.individual.yearly, ratio) },
  family: { monthly: ratioPrice(PRICES_USD_CENTS.family.monthly, ratio), yearly: ratioPrice(PRICES_USD_CENTS.family.yearly, ratio) },
});

export const TIER2_RATIO = 0.7;
export const TIER3_RATIO = 0.4;

const US_PRICES: RegionPrices = {
  currency: 'USD',
  tier: 'tier1',
  prices: {
    individual: { monthly: usd(PRICES_USD_CENTS.individual.monthly), yearly: usd(PRICES_USD_CENTS.individual.yearly) },
    family: { monthly: usd(PRICES_USD_CENTS.family.monthly), yearly: usd(PRICES_USD_CENTS.family.yearly) },
  },
};
const GB_PRICES: RegionPrices = { currency: 'GBP', tier: 'tier1', prices: { individual: { monthly: 8.99, yearly: 54.99 }, family: { monthly: 11.99, yearly: 74.99 } } };
const EU_PRICES: RegionPrices = { currency: 'EUR', tier: 'tier1', prices: { individual: { monthly: 9.99, yearly: 59.99 }, family: { monthly: 12.99, yearly: 79.99 } } };
const VN_PRICES: RegionPrices = { currency: 'VND', tier: 'tier3', prices: { individual: { monthly: 79000, yearly: 499000 }, family: { monthly: 109000, yearly: 699000 } } };
const TIER2_PRICES: RegionPrices = { currency: 'USD', tier: 'tier2', prices: scaled(TIER2_RATIO) };
const TIER3_PRICES: RegionPrices = { currency: 'USD', tier: 'tier3', prices: scaled(TIER3_RATIO) };

const TIER1_USD = ['US', 'CA', 'AU', 'NZ'];
/** EU-27 + EEA / Nordics shown in EUR (prices include VAT). */
const EUR_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT',
  'RO', 'SK', 'SI', 'ES', 'SE', 'NO', 'IS', 'LI', 'CH',
];
/** Low-price markets (≈40 %). VN has its own VND table. */
export const TIER3_COUNTRIES = [
  'ID', 'PH', 'IN', 'TH', 'MY', 'BD', 'PK', 'LK', 'NP', 'KH', 'LA', 'MM', 'BR', 'MX', 'AR', 'CO', 'PE', 'EC', 'BO', 'PY', 'VE',
  'EG', 'MA', 'TN', 'DZ', 'NG', 'KE', 'GH', 'ZA', 'TR', 'UA', 'KZ', 'UZ',
];

/** Regional price table for an ISO 3166-1 alpha-2 country (missing / invalid → US). Unlisted countries are tier 2. */
export function regionPrices(country?: string | null): RegionPrices {
  const c = (country ?? '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(c) || TIER1_USD.includes(c)) return US_PRICES;
  if (c === 'GB' || c === 'UK') return GB_PRICES;
  if (c === 'VN') return VN_PRICES;
  if (EUR_COUNTRIES.includes(c)) return EU_PRICES;
  if (TIER3_COUNTRIES.includes(c)) return TIER3_PRICES;
  return TIER2_PRICES;
}

/** Normalised country used in responses ('US' when missing / invalid). */
export const normalizeCountry = (country?: string | null) => {
  const c = (country ?? '').trim().toUpperCase();
  return /^[A-Z]{2}$/.test(c) ? c : 'US';
};

const FRACTIONLESS = new Set(['VND', 'JPY', 'KRW', 'CLP', 'ISK', 'UGX', 'PYG']);
/** Digits after the decimal point for a currency (VND / JPY / KRW... have none; Stripe amounts use the same rule). */
export const currencyMinorDigits = (currency: string) => (FRACTIONLESS.has(currency.toUpperCase()) ? 0 : 2);

/** Round to the currency's minor unit. */
export const roundMoney = (amount: number, currency: string) => {
  const f = 10 ** currencyMinorDigits(currency);
  return Math.round(amount * f) / f;
};

function localeFor(lang: string, country: string): string {
  const l = (lang || 'en').slice(0, 2).toLowerCase();
  const tag = `${l}-${country}`;
  try {
    return Intl.NumberFormat.supportedLocalesOf([tag]).length ? tag : l;
  } catch {
    return 'en';
  }
}

/** Intl price string, e.g. formatMoney(699000, 'VND', 'vi', 'VN') → "699.000 ₫". */
export function formatMoney(amount: number, currency: string, lang = 'en', country = 'US'): string {
  const digits = currencyMinorDigits(currency);
  try {
    return new Intl.NumberFormat(localeFor(lang, country), { style: 'currency', currency, minimumFractionDigits: digits, maximumFractionDigits: digits }).format(amount);
  } catch {
    return `${amount.toFixed(digits)} ${currency}`;
  }
}

export const PLAN_DISPLAY_NAME: Record<PlanId, L> = {
  individual: { en: 'Plus', vi: 'Plus', 'zh-Hans': 'Plus' },
  family: { en: 'Family', vi: 'Gia đình', 'zh-Hans': '家庭版' },
};

/** Trial rule (decision 2026-10-18): only yearly packages carry a free trial. */
export function trialFor(period: BillingPeriod, trialDays: number = TRIAL_DAYS): { days: number } | null {
  return period === 'yearly' && trialDays > 0 ? { days: trialDays } : null;
}

export interface OfferPackage {
  plan: PlanId;
  period: BillingPeriod;
  productId: string;
  displayName: L;
  priceString: string;
  price: number;
  currency: string;
  pricePerMonthString: string;
  /** Yearly: saving vs 12 × monthly of the same plan (floored percent); monthly: 0. */
  discountPercent: number;
  trial: { days: number } | null;
}

/** One package from a price (any source: table, Stripe, store). `monthlyPrice` = the plan's monthly price (for the discount). */
export function makePackage(o: {
  plan: PlanId;
  period: BillingPeriod;
  price: number;
  currency: string;
  monthlyPrice?: number | null;
  lang?: string;
  country?: string;
  trialDays?: number;
}): OfferPackage {
  const currency = o.currency.toUpperCase();
  const lang = o.lang ?? 'en';
  const country = normalizeCountry(o.country);
  const perMonth = o.period === 'yearly' ? roundMoney(o.price / 12, currency) : o.price;
  const discountPercent = o.period === 'yearly' && o.monthlyPrice && o.monthlyPrice > 0 ? Math.max(0, Math.floor((1 - o.price / (o.monthlyPrice * 12)) * 100)) : 0;
  return {
    plan: o.plan,
    period: o.period,
    productId: productId(o.plan, o.period),
    displayName: PLAN_DISPLAY_NAME[o.plan],
    priceString: formatMoney(o.price, currency, lang, country),
    price: o.price,
    currency,
    pricePerMonthString: formatMoney(perMonth, currency, lang, country),
    discountPercent,
    trial: trialFor(o.period, o.trialDays ?? TRIAL_DAYS),
  };
}

/** Display order on the paywall: Family first (default selection), yearly before monthly. */
export const PACKAGE_ORDER: readonly { plan: PlanId; period: BillingPeriod }[] = [
  { plan: 'family', period: 'yearly' },
  { plan: 'family', period: 'monthly' },
  { plan: 'individual', period: 'yearly' },
  { plan: 'individual', period: 'monthly' },
];

export interface LocalizedOffers {
  country: string;
  currency: string;
  tier: PriceTier;
  packages: OfferPackage[];
}

/**
 * The four packages for a country, priced from the regional table and formatted for `lang`.
 * `trialDays` applies to yearly packages only (7 by default, 14 for invited families).
 */
export function localizedOffers(country?: string | null, lang = 'en', opts: { trialDays?: number } = {}): LocalizedOffers {
  const c = normalizeCountry(country);
  const r = regionPrices(c);
  return {
    country: c,
    currency: r.currency,
    tier: r.tier,
    packages: PACKAGE_ORDER.map(({ plan, period }) =>
      makePackage({ plan, period, price: r.prices[plan][period], currency: r.currency, monthlyPrice: r.prices[plan].monthly, lang, country: c, trialDays: opts.trialDays }),
    ),
  };
}

/** Regional price string of one product (trial reminders, notices). */
export function priceStringFor(plan: PlanId, period: BillingPeriod, country?: string | null, lang = 'en'): { price: number; currency: string; priceString: string } {
  const c = normalizeCountry(country);
  const r = regionPrices(c);
  const price = r.prices[plan][period];
  return { price, currency: r.currency, priceString: formatMoney(price, r.currency, lang, c) };
}

/** Which RevenueCat offering a user should see (server decides; the native SDK fetches it by id). */
export function offeringFor(o: { invited: boolean; placement?: string | null }): OfferingId {
  if (o.invited) return 'invited_14d';
  if (o.placement === 'onboarding_alive') return 'onboarding';
  return 'default';
}
